import React, {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import swal from "sweetalert";

/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
  "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const MIN_SHARE = 1;
const MAX_SHARE = 10000;

/* =========================================================
   HELPERS
========================================================= */

const numberFormat = (value) => {
  const number = Number(value || 0);

  if (!Number.isFinite(number)) {
    return "0";
  }

  return new Intl.NumberFormat("en-BD", {
    maximumFractionDigits: 2,
  }).format(number);
};

const money = (value) => {
  return `৳${numberFormat(value)}`;
};

const safeText = (value, fallback = "—") => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return fallback;
  }

  return value;
};

const getError = (value) => {
  if (!value) return "";

  if (Array.isArray(value)) {
    return value[0] || "";
  }

  if (typeof value === "string") {
    return value;
  }

  return "";
};

/* =========================================================
   ICON
========================================================= */

const ProjectIcon = ({ type }) => {
  const projectType = String(type || "").toLowerCase();

  if (projectType.includes("hospital")) {
    return "✚";
  }

  if (
    projectType.includes("farm") ||
    projectType.includes("agro")
  ) {
    return "♧";
  }

  if (
    projectType.includes("hotel") ||
    projectType.includes("resort")
  ) {
    return "⌂";
  }

  if (
    projectType.includes("land") ||
    projectType.includes("housing")
  ) {
    return "◇";
  }

  return "▦";
};

/* =========================================================
   COMPONENT
========================================================= */

const Checkout = () => {
  const authToken = localStorage.getItem("auth_token");

  const [projects, setProjects] = useState([]);

  const [selectedProjectId, setSelectedProjectId] =
    useState("");

  const [form, setForm] = useState({
    amount: "",
    password: "",
    error_list: {},
  });

  const [termsAccepted, setTermsAccepted] =
    useState(false);

  const [showPassword, setShowPassword] =
    useState(false);

  const [fetchLoading, setFetchLoading] =
    useState(true);

  const [submitting, setSubmitting] =
    useState(false);

  const [pageError, setPageError] =
    useState("");

  /* =======================================================
     LOAD PROJECTS
  ======================================================= */

  useEffect(() => {
    let mounted = true;

    const loadProjects = async () => {
      if (!authToken) {
        if (mounted) {
          setFetchLoading(false);

          setPageError(
            "Your login session was not found. Please login again."
          );
        }

        return;
      }

      try {
        setFetchLoading(true);
        setPageError("");

        const response = await axios.post(
          "/invest-packege",
          {
            key_s: "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9",
          }
        );

        if (!mounted) return;

        if (response?.data?.status === "success") {
          const rows = Array.isArray(
            response?.data?.data
          )
            ? response.data.data
            : [];

          setProjects(rows);
        } else {
          setProjects([]);

          setPageError(
            response?.data?.message ||
              "Unable to load share projects."
          );
        }
      } catch (error) {
        console.error(
          "Project loading error:",
          error
        );

        if (!mounted) return;

        setProjects([]);

        setPageError(
          error?.response?.data?.message ||
            "Unable to load share projects. Please try again."
        );
      } finally {
        if (mounted) {
          setFetchLoading(false);
        }
      }
    };

    loadProjects();

    return () => {
      mounted = false;
    };
  }, [authToken]);

  /* =======================================================
     SELECTED PROJECT
  ======================================================= */

  const selectedProject = useMemo(() => {
    return (
      projects.find(
        (project) =>
          String(project.id) ===
          String(selectedProjectId)
      ) || null
    );
  }, [projects, selectedProjectId]);

  /* =======================================================
     CALCULATIONS
  ======================================================= */

  const sharePrice = Number(
    selectedProject?.price || 0
  );

  const bookingAmount = Number(
    selectedProject?.booking_amount || 0
  );

  const enteredAmount = Number(form.amount || 0);

  const totalPayable = enteredAmount;

  const offerShares = Number(
    selectedProject?.offer_shares || 0
  );

  const permanentPercentage = Number(
    selectedProject?.permanent_percentage || 0
  );

  const minimumBooking = bookingAmount > 0 ? bookingAmount : 1;
  const maximumBooking = sharePrice > 0 ? sharePrice : 999999999;

  /* =======================================================
     SELECT PROJECT
  ======================================================= */

  const selectProject = (project) => {
    if (!project) return;

    setSelectedProjectId(String(project.id));

    const minimum = Number(project.booking_amount || 0);

    setForm((previous) => ({
      ...previous,
      amount: minimum > 0 ? minimum : "",
      error_list: {
        ...previous.error_list,
        selectedpackege: "",
        amount: "",
      },
    }));
  };

  /* =======================================================
     INPUT
  ======================================================= */

  const handleInput = (event) => {
    const { name } = event.target;
    let { value } = event.target;

    if (name === "amount") {
      value = value.replace(/[^0-9.]/g, "");
    }

    setForm((previous) => ({
      ...previous,
      [name]: value,
      error_list: {
        ...previous.error_list,
        [name]: "",
      },
    }));
  };

  const setQuickAmount = (amount) => {
    if (!selectedProject) return;

    const safeAmount = Math.min(
      maximumBooking,
      Math.max(minimumBooking, Number(amount || 0))
    );

    setForm((previous) => ({
      ...previous,
      amount: safeAmount,
      error_list: {
        ...previous.error_list,
        amount: "",
      },
    }));
  };

  /* =======================================================
     VALIDATION
  ======================================================= */

  const validateForm = () => {
    const errors = {};

    if (!selectedProject) {
      errors.selectedpackege = "Please select a share project.";
    }

    const amount = Number(form.amount);

    if (!amount || amount <= 0) {
      errors.amount = "Please enter a booking amount.";
    } else if (selectedProject && amount < minimumBooking) {
      errors.amount = `Minimum booking amount is ${money(minimumBooking)}.`;
    } else if (selectedProject && sharePrice > 0 && amount > sharePrice) {
      errors.amount = `Booking amount cannot exceed ${money(sharePrice)}.`;
    }

    if (!form.password || !form.password.trim()) {
      errors.password = "Account password is required.";
    }

    if (!termsAccepted) {
      errors.terms = "Please accept the terms and conditions.";
    }

    setForm((previous) => ({
      ...previous,
      error_list: errors,
    }));

    return Object.keys(errors).length === 0;
  };

  /* =======================================================
     SUBMIT
  ======================================================= */

  const submitSharePurchase = async (
    event
  ) => {
    event.preventDefault();

    if (submitting) return;

    if (!authToken) {
      swal(
        "Session Expired",
        "Please login again to continue.",
        "warning"
      );

      return;
    }

    if (!validateForm()) {
      return;
    }

    /*
     * amount is sent for compatibility with your
     * existing backend.
     *
     * IMPORTANT:
     * Laravel should independently calculate:
     * project.price * numberc
     *
     * Do not trust this frontend amount alone.
     */
    const data = {
      selectedpackege:
        selectedProject.id,

      numberc: 1,

      amount: totalPayable,

      password: form.password,

      key_s: API_KEY,
    };

    try {
      setSubmitting(true);

      const response = await axios.post(
        "/sub-invest",
        data
      );

      const result =
        response?.data || {};

      if (
        result.status === "success"
      ) {
        await swal(
          "Share Purchase Successful",
          result.message ||
            "Your share purchase has been completed successfully.",
          "success"
        );

        setSelectedProjectId("");

        setForm({
          amount: "",
          password: "",
          error_list: {},
        });

        setTermsAccepted(false);
        setShowPassword(false);
      } else if (
        result.status === "fail"
      ) {
        swal(
          "Unable to Purchase",
          result.message ||
            "Your share purchase could not be completed.",
          "warning"
        );
      } else if (
        result.validation_error
      ) {
        setForm((previous) => ({
          ...previous,

          error_list:
            result.validation_error ||
            {},
        }));
      } else {
        swal(
          "Unable to Continue",
          result.message ||
            "Something went wrong.",
          "warning"
        );
      }
    } catch (error) {
      console.error(
        "Share purchase error:",
        error
      );

      if (
        error?.response?.status === 422
      ) {
        const validationErrors =
          error?.response?.data?.errors ||
          error?.response?.data
            ?.validation_error ||
          {};

        setForm((previous) => ({
          ...previous,

          error_list:
            validationErrors,
        }));

        return;
      }

      swal(
        "Something Went Wrong",
        error?.response?.data?.message ||
          "We could not process your share purchase. Please try again.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =======================================================
     ERRORS
  ======================================================= */

  const selectedError = getError(
    form.error_list?.selectedpackege
  );

  const amountError = getError(
    form.error_list?.amount
  );

  const passwordError = getError(
    form.error_list?.password
  );

  const termsError = getError(
    form.error_list?.terms
  );

  /* =======================================================
     SUBMIT STATUS
  ======================================================= */

  const canSubmit =
    Boolean(selectedProject) &&
    Number(form.amount) >= minimumBooking &&
    (sharePrice <= 0 || Number(form.amount) <= sharePrice) &&
    Boolean(form.password.trim()) &&
    termsAccepted &&
    totalPayable > 0 &&
    !submitting;

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <Fragment>
      <style>{`

        /* ================================================
           ROOT
        ================================================ */

        .share-buy-page {
          --primary: #635bff;
          --primary-dark: #5148e5;
          --primary-soft: #f1f0ff;

          --green: #12b76a;
          --green-soft: #ecfdf3;

          --warning: #f79009;
          --warning-soft: #fff8eb;

          --danger: #f04438;

          --text: #182230;
          --text2: #344054;
          --muted: #667085;

          --border: #e6e9ef;
          --soft: #f7f8fb;
          --white: #ffffff;

          min-height: 100vh;

          background:
            radial-gradient(
              circle at 0 0,
              rgba(99,91,255,.08),
              transparent 26%
            ),
            radial-gradient(
              circle at 100% 0,
              rgba(18,183,106,.06),
              transparent 25%
            ),
            #f7f8fb;

          color: var(--text);

          font-family:
            Inter,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
        }

        .share-buy-shell {
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
        }


        /* ================================================
           HERO
        ================================================ */

        .share-hero {
          position: relative;
          overflow: hidden;

          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 30px;

          min-height: 220px;

          margin-bottom: 22px;

          padding: 34px 36px;

          border-radius: 25px;

          background:
            linear-gradient(
              135deg,
              #161b2d 0%,
              #252b48 58%,
              #393467 100%
            );

          box-shadow:
            0 18px 50px
            rgba(30,38,65,.12);
        }

        .share-hero::before {
          content: "";

          position: absolute;

          width: 350px;
          height: 350px;

          right: -130px;
          top: -210px;

          border-radius: 50%;

          background:
            rgba(255,255,255,.065);
        }

        .share-hero::after {
          content: "";

          position: absolute;

          width: 220px;
          height: 220px;

          right: 250px;
          bottom: -170px;

          border-radius: 50%;

          background:
            rgba(99,91,255,.22);
        }

        .hero-content {
          position: relative;
          z-index: 2;

          max-width: 760px;
        }

        .hero-small {
          display: flex;
          align-items: center;
          gap: 9px;

          margin-bottom: 10px;

          color: #c8c4ff;

          font-size: 14px;
          font-weight: 800;

          text-transform: uppercase;

          letter-spacing: .7px;
        }

        .hero-small-dot {
          width: 9px;
          height: 9px;

          border-radius: 50%;

          background: #75e5aa;

          box-shadow:
            0 0 0 5px
            rgba(117,229,170,.12);
        }

        .share-hero h1 {
          margin: 0 0 10px;

          color: #fff;

          font-size: 34px;
          font-weight: 900;

          line-height: 1.2;
        }

        .share-hero p {
          max-width: 700px;

          margin: 0;

          color:
            rgba(255,255,255,.7);

          font-size: 16px;

          line-height: 1.7;
        }

        .hero-secure {
          position: relative;
          z-index: 2;

          flex: 0 0 220px;

          padding: 18px;

          border:
            1px solid
            rgba(255,255,255,.12);

          border-radius: 18px;

          background:
            rgba(255,255,255,.075);

          backdrop-filter: blur(8px);
        }

        .hero-secure-icon {
          width: 45px;
          height: 45px;

          display: flex;
          align-items: center;
          justify-content: center;

          margin-bottom: 12px;

          border-radius: 13px;

          background:
            rgba(117,229,170,.13);

          color: #75e5aa;

          font-size: 21px;
          font-weight: 900;
        }

        .hero-secure small {
          display: block;

          margin-bottom: 3px;

          color:
            rgba(255,255,255,.55);

          font-size: 13px;
        }

        .hero-secure strong {
          display: block;

          color: #fff;

          font-size: 16px;
          font-weight: 850;
        }


        /* ================================================
           LAYOUT
        ================================================ */

        .share-main-grid {
          display: grid;

          grid-template-columns:
            minmax(0,1fr)
            380px;

          gap: 22px;

          align-items: start;
        }

        .share-panel {
          overflow: hidden;

          border:
            1px solid
            var(--border);

          border-radius: 22px;

          background: #fff;

          box-shadow:
            0 8px 30px
            rgba(30,38,64,.05);
        }

        .share-panel-body {
          padding: 25px;
        }


        /* ================================================
           SECTION
        ================================================ */

        .share-section-head {
          display: flex;

          align-items: flex-start;

          gap: 13px;

          margin-bottom: 20px;
        }

        .share-step {
          flex: 0 0 43px;

          width: 43px;
          height: 43px;

          display: flex;

          align-items: center;
          justify-content: center;

          border-radius: 13px;

          background:
            var(--primary-soft);

          color: var(--primary);

          font-size: 15px;
          font-weight: 900;
        }

        .share-section-head h2 {
          margin: 0 0 4px;

          color: var(--text);

          font-size: 22px;
          font-weight: 850;

          line-height: 1.3;
        }

        .share-section-head p {
          margin: 0;

          color: var(--muted);

          font-size: 14px;

          line-height: 1.55;
        }


        /* ================================================
           PROJECT GRID
        ================================================ */

        .project-grid {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          gap: 16px;
          width: 100%;
          overflow-x: auto;
          padding-bottom: 10px;
          scroll-behavior: smooth;
        }

        .project-grid .project-card {
          flex: 0 0 320px;
          width: 320px;
          min-width: 320px;
        }

        .project-card {
          position: relative;

          overflow: hidden;

          display: flex;

          flex-direction: column;

          padding: 20px;

          border:
            1.5px solid
            var(--border);

          border-radius: 19px;

          background: #fff;

          cursor: pointer;

          transition:
            .22s ease;
        }

        .project-card:hover {
          transform:
            translateY(-3px);

          border-color:
            #c8c3ff;

          box-shadow:
            0 13px 30px
            rgba(65,55,140,.09);
        }

        .project-card.selected {
          border-color:
            var(--primary);

          background:
            linear-gradient(
              180deg,
              #f8f7ff 0%,
              #fff 100%
            );

          box-shadow:
            0 0 0 3px
            rgba(99,91,255,.08);
        }

        .project-radio {
          position: absolute;

          opacity: 0;

          pointer-events: none;
        }

        .project-check {
          position: absolute;

          top: 17px;
          right: 17px;

          width: 29px;
          height: 29px;

          display: flex;
          align-items: center;
          justify-content: center;

          border:
            2px solid #d6dae2;

          border-radius: 50%;

          background: #fff;

          color: transparent;

          font-size: 14px;
          font-weight: 900;
        }

        .project-card.selected
        .project-check {
          border-color:
            var(--primary);

          background:
            var(--primary);

          color: #fff;
        }

        .project-top {
          display: flex;

          align-items: center;

          gap: 11px;

          padding-right: 40px;

          margin-bottom: 14px;
        }

        .project-icon {
          flex: 0 0 47px;

          width: 47px;
          height: 47px;

          display: flex;

          align-items: center;
          justify-content: center;

          border-radius: 14px;

          background:
            var(--primary-soft);

          color: var(--primary);

          font-size: 23px;
          font-weight: 900;
        }

        .project-type {
          margin-bottom: 2px;

          color: var(--primary);

          font-size: 12px;
          font-weight: 800;

          text-transform: uppercase;

          letter-spacing: .45px;
        }

        .project-name {
          color: var(--text);

          font-size: 17px;
          font-weight: 850;

          line-height: 1.35;
        }

        .project-description {
          flex: 1;

          margin-bottom: 17px;

          color: var(--muted);

          font-size: 14px;

          line-height: 1.65;
        }


        /* ================================================
           PROJECT PRICE
        ================================================ */

        .project-price-box {
          padding: 14px;

          margin-bottom: 13px;

          border-radius: 14px;

          background: #f7f7ff;
        }

        .project-price-box span {
          display: block;

          margin-bottom: 3px;

          color: var(--muted);

          font-size: 12px;
        }

        .project-price-box strong {
          color: var(--primary);

          font-size: 23px;
          font-weight: 900;
        }

        .project-price-box small {
          margin-left: 5px;

          color: var(--muted);

          font-size: 12px;
          font-weight: 600;
        }


        /* ================================================
           PROJECT INFO
        ================================================ */

        .project-info-grid {
          display: grid;

          grid-template-columns:
            1fr 1fr;

          gap: 9px;
        }

        .project-info {
          padding: 10px;

          border:
            1px solid #edf0f4;

          border-radius: 11px;

          background: #fbfcfd;
        }

        .project-info span {
          display: block;

          margin-bottom: 2px;

          color: var(--muted);

          font-size: 11px;
        }

        .project-info strong {
          display: block;

          color: var(--text2);

          font-size: 13px;
          font-weight: 800;
        }

        .project-offer {
          color:
            #b54708 !important;
        }

        .project-permanent {
          color:
            #079455 !important;
        }


        /* ================================================
           DIVIDER
        ================================================ */

        .share-divider {
          height: 1px;

          margin: 28px 0;

          background:
            var(--border);
        }


        /* ================================================
           PURCHASE DETAILS
        ================================================ */

        .purchase-grid {
          display: grid;

          grid-template-columns:
            1fr 1fr;

          gap: 17px;
        }

        .field-label {
          display: block;

          margin-bottom: 8px;

          color: var(--text2);

          font-size: 15px;
          font-weight: 800;
        }

        .quantity-box {
          height: 55px;

          display: grid;

          grid-template-columns:
            52px 1fr 52px;

          overflow: hidden;

          border:
            1.5px solid #dfe3ea;

          border-radius: 13px;

          background: #fff;
        }

        .quantity-button {
          border: 0;

          background: #f7f8fb;

          color: var(--text);

          font-size: 23px;
          font-weight: 800;

          cursor: pointer;
        }

        .quantity-button:hover:not(:disabled) {
          background:
            var(--primary-soft);

          color: var(--primary);
        }

        .quantity-button:disabled {
          cursor: not-allowed;

          opacity: .4;
        }

        .quantity-input {
          width: 100%;

          border: 0;

          outline: 0;

          background: #fff;

          color: var(--text);

          text-align: center;

          font-size: 17px;
          font-weight: 850;
        }

        .quick-quantity {
          display: flex;

          flex-wrap: wrap;

          gap: 7px;

          margin-top: 9px;
        }

        .quick-quantity button {
          padding: 6px 10px;

          border:
            1px solid
            var(--border);

          border-radius: 8px;

          background: #fff;

          color: var(--text2);

          font-size: 12px;
          font-weight: 750;

          cursor: pointer;
        }

        .quick-quantity button:hover {
          border-color:
            var(--primary);

          color: var(--primary);
        }


        .booking-amount-wrap {
          position: relative;
        }

        .booking-currency {
          position: absolute;
          left: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--primary);
          font-size: 21px;
          font-weight: 900;
          z-index: 2;
        }

        .booking-amount-input {
          width: 100%;
          height: 55px;
          padding: 0 15px 0 42px;
          border: 1.5px solid #dfe3ea;
          border-radius: 13px;
          outline: 0;
          background: #fff;
          color: var(--text);
          font-size: 18px;
          font-weight: 850;
        }

        .booking-amount-input:focus {
          border-color: var(--primary);
          box-shadow: 0 0 0 4px rgba(99,91,255,.08);
        }

        .booking-amount-input:disabled {
          background: #f7f8fb;
          cursor: not-allowed;
        }

        .booking-calculation {
          grid-template-columns: repeat(3, 1fr);
        }

        /* ================================================
           PASSWORD
        ================================================ */

        .password-wrap {
          position: relative;
        }

        .password-input {
          width: 100%;

          height: 55px;

          padding:
            0 78px
            0 15px;

          border:
            1.5px solid #dfe3ea;

          border-radius: 13px;

          outline: 0;

          background: #fff;

          color: var(--text);

          font-size: 15px;
          font-weight: 600;
        }

        .password-input:focus {
          border-color:
            var(--primary);

          box-shadow:
            0 0 0 4px
            rgba(99,91,255,.08);
        }

        .password-toggle {
          position: absolute;

          right: 8px;
          top: 9px;

          height: 37px;

          padding: 0 11px;

          border: 0;

          border-radius: 9px;

          background:
            var(--primary-soft);

          color: var(--primary);

          font-size: 12px;
          font-weight: 800;

          cursor: pointer;
        }

        .field-help {
          margin-top: 7px;

          color: var(--muted);

          font-size: 12px;

          line-height: 1.5;
        }

        .field-error {
          display: block;

          margin-top: 7px;

          color: var(--danger);

          font-size: 12px;
          font-weight: 700;
        }


        /* ================================================
           PURCHASE CALCULATION
        ================================================ */

        .calculation-box {
          display: grid;

          grid-template-columns:
            repeat(
              3,
              1fr
            );

          gap: 12px;

          margin-top: 21px;
        }

        .calculation-item {
          padding: 15px;

          border:
            1px solid
            var(--border);

          border-radius: 14px;

          background: #fafbfc;
        }

        .calculation-item span {
          display: block;

          margin-bottom: 5px;

          color: var(--muted);

          font-size: 12px;
        }

        .calculation-item strong {
          color: var(--text);

          font-size: 16px;
          font-weight: 850;
        }

        .calculation-item.total {
          border-color:
            #d9d5ff;

          background:
            var(--primary-soft);
        }

        .calculation-item.total strong {
          color: var(--primary);

          font-size: 18px;
        }


        /* ================================================
           TERMS
        ================================================ */

        .terms-box {
          display: flex;

          align-items: flex-start;

          gap: 11px;

          padding: 15px;

          margin-top: 20px;

          border:
            1px solid
            var(--border);

          border-radius: 14px;

          background: #f8f9fb;
        }

        .terms-box input {
          flex: 0 0 19px;

          width: 19px;
          height: 19px;

          margin-top: 2px;

          accent-color:
            var(--primary);

          cursor: pointer;
        }

        .terms-box label {
          margin: 0;

          color: #475467;

          font-size: 13px;

          line-height: 1.6;

          cursor: pointer;
        }


        /* ================================================
           BUTTON
        ================================================ */

        .buy-share-button {
          width: 100%;

          min-height: 57px;

          display: flex;

          align-items: center;
          justify-content: center;

          gap: 10px;

          margin-top: 19px;

          padding: 12px 20px;

          border: 0;

          border-radius: 14px;

          background:
            linear-gradient(
              135deg,
              var(--primary),
              var(--primary-dark)
            );

          color: #fff;

          font-size: 16px;
          font-weight: 900;

          cursor: pointer;

          box-shadow:
            0 11px 25px
            rgba(99,91,255,.20);

          transition:
            .2s ease;
        }

        .buy-share-button:hover:not(:disabled) {
          transform:
            translateY(-1px);

          box-shadow:
            0 15px 30px
            rgba(99,91,255,.25);
        }

        .buy-share-button:disabled {
          opacity: .5;

          cursor: not-allowed;

          box-shadow: none;
        }

        .button-spinner {
          width: 20px;
          height: 20px;

          border:
            2px solid
            rgba(255,255,255,.35);

          border-top-color: #fff;

          border-radius: 50%;

          animation:
            shareSpin
            .7s linear infinite;
        }

        @keyframes shareSpin {
          to {
            transform:
              rotate(360deg);
          }
        }


        /* ================================================
           SUMMARY
        ================================================ */

        .summary-panel {
          position: sticky;

          top: 20px;
        }

        .summary-header {
          padding: 23px;

          background:
            linear-gradient(
              135deg,
              #171c2e,
              #303655
            );

          color: #fff;
        }

        .summary-header small {
          display: block;

          margin-bottom: 4px;

          color:
            rgba(255,255,255,.6);

          font-size: 12px;
        }

        .summary-header h2 {
          margin: 0;

          color: #fff;

          font-size: 22px;
          font-weight: 850;
        }

        .summary-body {
          padding: 21px;
        }

        .summary-project {
          padding: 15px;

          margin-bottom: 14px;

          border:
            1px solid #e8e5ff;

          border-radius: 14px;

          background: #f8f7ff;
        }

        .summary-project small {
          display: block;

          margin-bottom: 4px;

          color: var(--muted);

          font-size: 11px;

          text-transform: uppercase;

          font-weight: 750;

          letter-spacing: .5px;
        }

        .summary-project strong {
          display: block;

          color: var(--text);

          font-size: 15px;
          font-weight: 850;

          line-height: 1.4;
        }

        .summary-project p {
          margin:
            7px 0 0;

          color: var(--muted);

          font-size: 12px;

          line-height: 1.55;
        }

        .summary-row {
          display: flex;

          align-items: flex-start;
          justify-content: space-between;

          gap: 15px;

          padding: 12px 1px;

          border-bottom:
            1px solid #eef0f4;
        }

        .summary-row span {
          color: var(--muted);

          font-size: 13px;
        }

        .summary-row strong {
          max-width: 55%;

          color: var(--text2);

          font-size: 13px;
          font-weight: 800;

          text-align: right;

          overflow-wrap: anywhere;
        }

        .summary-row
        strong.green {
          color: #079455;
        }

        .summary-row
        strong.orange {
          color: #b54708;
        }


        /* ================================================
           SUMMARY TOTAL
        ================================================ */

        .summary-total {
          margin-top: 17px;

          padding: 18px;

          border-radius: 15px;

          background:
            linear-gradient(
              135deg,
              #1a2034,
              #313758
            );
        }

        .summary-total span {
          display: block;

          margin-bottom: 5px;

          color:
            rgba(255,255,255,.62);

          font-size: 13px;
        }

        .summary-total strong {
          display: block;

          color: #fff;

          font-size: 27px;
          font-weight: 900;

          line-height: 1.2;
        }

        .summary-note {
          padding: 13px;

          margin-top: 14px;

          border:
            1px solid
            #ffebc2;

          border-radius: 12px;

          background:
            var(--warning-soft);

          color: #8a5a00;

          font-size: 12px;

          line-height: 1.6;
        }


        /* ================================================
           STATUS / LOADING
        ================================================ */

        .project-status {
          padding: 35px 20px;

          border:
            1px dashed
            #d8dce5;

          border-radius: 16px;

          background: #fbfcfd;

          text-align: center;

          color: var(--muted);

          font-size: 14px;
        }

        .project-status strong {
          display: block;

          margin-bottom: 5px;

          color: var(--text);

          font-size: 16px;
        }

        .project-loading-grid {
          display: grid;

          grid-template-columns:
            1fr 1fr;

          gap: 16px;
        }

        .project-skeleton {
          height: 320px;

          border-radius: 19px;

          background:
            linear-gradient(
              90deg,
              #f1f2f5 25%,
              #fafafa 37%,
              #f1f2f5 63%
            );

          background-size:
            400% 100%;

          animation:
            projectShimmer
            1.35s ease infinite;
        }

        @keyframes projectShimmer {
          0% {
            background-position:
              100% 0;
          }

          100% {
            background-position:
              -100% 0;
          }
        }


        /* ================================================
           TABLET
        ================================================ */

        @media(max-width:1100px) {

          .share-main-grid {
            grid-template-columns:
              1fr;
          }

          .summary-panel {
            position: static;
          }
        }


        /* ================================================
           MOBILE
        ================================================ */

        @media(max-width:750px) {

          .share-buy-page {
            padding: 13px;
          }

          .share-hero {
            flex-direction: column;

            align-items: flex-start;

            min-height: auto;

            padding: 25px 20px;

            border-radius: 19px;
          }

          .share-hero h1 {
            font-size: 27px;
          }

          .share-hero p {
            font-size: 14px;
          }

          .hero-secure {
            width: 100%;

            flex-basis: auto;
          }

          .share-panel-body {
            padding: 18px;
          }

          .project-grid,
          .project-loading-grid {
            grid-template-columns:
              1fr;
          }

          .purchase-grid {
            grid-template-columns:
              1fr;
          }

          .calculation-box {
            grid-template-columns:
              1fr;
          }

          .share-section-head h2 {
            font-size: 19px;
          }

          .project-card {
            min-height: auto;
          }

          .summary-total strong {
            font-size: 24px;
          }
        }

      `}</style>

      <main className="share-buy-page" style={{background: "white",borderRadius: "20px"}}>
        <div className="share-buy-shell" >

          <div className="">

            {/* ===========================================
                LEFT
            =========================================== */}

            <section className="share-panel">

              <div className="share-panel-body">

                {/* =======================================
                    STEP 1
                ======================================= */}

                <div className="share-section-head">

                  <div className="share-step">
                    01
                  </div>

                  <div>

                    <h2>
                      Select Share Project
                    </h2>

                    <p>
                      Review the available projects,
                      share prices and project
                      information.
                    </p>

                  </div>

                </div>


                {/* =======================================
                    PROJECTS
                ======================================= */}

                {fetchLoading ? (

                  <div className="project-loading-grid">

                    <div className="project-skeleton"></div>

                    <div className="project-skeleton"></div>

                    <div className="project-skeleton"></div>

                    <div className="project-skeleton"></div>

                  </div>

                ) : pageError ? (

                  <div className="project-status">

                    <strong>
                      Unable to Load Projects
                    </strong>

                    {pageError}

                  </div>

                ) : projects.length === 0 ? (

                  <div className="project-status">

                    <strong>
                      No Share Project Available
                    </strong>

                    Please check again later.

                  </div>

                ) : (

                  <div className="project-grid">

                    {projects.map(
                      (project, index) => {

                        const selected =
                          String(
                            selectedProjectId
                          ) ===
                          String(project.id);

                        return (

                          <label
                            key={
                              project.id ||
                              index
                            }
                            className={
                              `project-card ${
                                selected
                                  ? "selected"
                                  : ""
                              }`
                            }
                          >

                            <input
                              type="radio"
                              name="selectedProject"
                              className="project-radio"
                              value={project.id}
                              checked={selected}
                              onChange={() =>
                                selectProject(
                                  project
                                )
                              }
                            />

                            <span className="project-check">
                              ✓
                            </span>


                            <div className="project-top">

                              <div className="project-icon">

                                <ProjectIcon
                                  type={
                                    project.project_type
                                  }
                                />

                              </div>

                              <div>

                                <div className="project-type">

                                  {safeText(
                                    project.project_type,
                                    "Share Project"
                                  )}

                                </div>

                                <div className="project-name">

                                  {
                                    project.packege_name
                                  }

                                </div>

                              </div>

                            </div>


                            <div className="project-price-box">

                              <span>
                                Price Per Share
                              </span>

                              <strong>

                                {money(
                                  project.price
                                )}

                              </strong>

                              <small>
                                / Share
                              </small>

                            </div>


                            <div className="project-info-grid">


                              <div className="project-info">

                                <span>
                                  Booking From
                                </span>

                                <strong>

                                  {money(
                                    project.booking_amount
                                  )}

                                </strong>

                              </div>




                            </div>

                          </label>

                        );
                      }
                    )}

                  </div>

                )}


                {selectedError && (

                  <span className="field-error">
                    {selectedError}
                  </span>

                )}


                <div className="share-divider"></div>


                {/* =======================================
                    STEP 2
                ======================================= */}

                <div className="share-section-head">

                  <div className="share-step">
                    02
                  </div>

                  <div>

                    <h2>
                      Share Purchase Details
                    </h2>

                    <p>
                      Select your share quantity and
                      confirm using your account
                      password.
                    </p>

                  </div>

                </div>


                <form
                  onSubmit={
                    submitSharePurchase
                  }
                  noValidate
                >

                  <div className="purchase-grid">

                    {/* ===================================
                        BOOKING AMOUNT
                    =================================== */}

                    <div>
                      <label className="field-label">
                        Booking Amount
                      </label>

                      <div className="booking-amount-wrap">
                        <span className="booking-currency">৳</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          name="amount"
                          className="booking-amount-input"
                          placeholder={
                            selectedProject
                              ? `Minimum ${money(minimumBooking)}`
                              : "Select a project first"
                          }
                          value={form.amount}
                          onChange={handleInput}
                          disabled={!selectedProject}
                        />
                      </div>

                      {selectedProject && (
                        <div className="quick-quantity">
                          <button
                            type="button"
                            onClick={() => setQuickAmount(minimumBooking)}
                          >
                            Minimum {money(minimumBooking)}
                          </button>

                          {sharePrice > minimumBooking && (
                            <button
                              type="button"
                              onClick={() => setQuickAmount(sharePrice)}
                            >
                              Full Share {money(sharePrice)}
                            </button>
                          )}
                        </div>
                      )}

                      <div className="field-help">
                        {selectedProject
                          ? `Booking starts from ${money(minimumBooking)}. Full share value is ${money(sharePrice)}.`
                          : "Select a project first."}
                      </div>

                      {amountError && (
                        <span className="field-error">
                          {amountError}
                        </span>
                      )}
                    </div>


                    {/* ===================================
                        PASSWORD
                    =================================== */}

                    <div>

                      <label className="field-label">
                        Account Password
                      </label>


                      <div className="password-wrap">

                        <input
                          type={
                            showPassword
                              ? "text"
                              : "password"
                          }
                          name="password"
                          className="password-input"
                          placeholder="Enter account password"
                          value={form.password}
                          onChange={
                            handleInput
                          }
                          autoComplete="current-password"
                        />


                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() =>
                            setShowPassword(
                              (previous) =>
                                !previous
                            )
                          }
                        >

                          {showPassword
                            ? "Hide"
                            : "Show"}

                        </button>

                      </div>


                      <div className="field-help">
                        Required to authorize the
                        share purchase.
                      </div>


                      {passwordError && (

                        <span className="field-error">
                          {passwordError}
                        </span>

                      )}

                    </div>

                  </div>


                  {/* =====================================
                      CALCULATION
                  ===================================== */}

                  <div className="calculation-box booking-calculation">
                    <div className="calculation-item">
                      <span>Minimum Booking</span>
                      <strong>
                        {selectedProject ? money(minimumBooking) : "—"}
                      </strong>
                    </div>

                    <div className="calculation-item">
                      <span>Full Share Value</span>
                      <strong>
                        {selectedProject ? money(sharePrice) : "—"}
                      </strong>
                    </div>

                    <div className="calculation-item total">
                      <span>Booking Amount</span>
                      <strong>
                        {selectedProject ? money(totalPayable) : "৳0"}
                      </strong>
                    </div>
                  </div>

                  <div className="terms-box">

                    <input
                      type="checkbox"
                      id="share-purchase-terms"
                      checked={
                        termsAccepted
                      }
                      onChange={(event) => {

                        setTermsAccepted(
                          event.target.checked
                        );

                        setForm(
                          (previous) => ({
                            ...previous,

                            error_list: {
                              ...previous.error_list,
                              terms: "",
                            },
                          })
                        );

                      }}
                    />


                    <label htmlFor="share-purchase-terms">

                      I have reviewed the
                      selected project, share
                      price, booking amount and
                      project information, and I
                      accept the applicable terms
                      and conditions.

                    </label>

                  </div>


                  {termsError && (

                    <span className="field-error">
                      {termsError}
                    </span>

                  )}



                  {/* =====================================
                      SUBMIT
                  ===================================== */}

                  <button
                    type="submit"
                    className="buy-share-button"
                    disabled={!canSubmit}
                  >

                    {submitting ? (
                      <>

                        <span className="button-spinner"></span>

                        Processing Booking...

                      </>
                    ) : selectedProject ? (

                      <>
                        Confirm Booking •{" "}
                        {money(
                          totalPayable
                        )}
                      </>

                    ) : (

                      "Select a Project to Continue"

                    )}

                  </button>

                </form>

              </div>

            </section>


          </div>

        </div>
      </main>

    </Fragment>
  );
};

export default Checkout;